import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';
import {
  fetchSapJobDetail,
  fetchSapJobMonitoring,
  fetchSapJobRuns,
  fetchSapJobTimingProfile,
} from '@/lib/sapJobsApi';
import { SAP_JOB_DETAIL_POLL_MS } from '@/lib/sapJobsConfig';
import {
  formatDurationSeconds,
  formatIsoDateTime,
  MONITORING_STAGE_LABELS,
} from '@/lib/sapJobStatus';
import SapJobStatusBadge from '@/components/sapJobs/SapJobStatusBadge';
import SapJobMonitoringPanel from '@/components/sapJobs/SapJobMonitoringPanel';
import SapJobTimingPanel from '@/components/sapJobs/SapJobTimingPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function runDetailPath(runId) {
  return `/sap-job-monitoring/runs/${encodeURIComponent(runId)}`;
}

function decodeSlotParam(raw) {
  try {
    return decodeURIComponent(raw || '');
  } catch {
    return raw || '';
  }
}

export default function SapJobDetail() {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('dashboard', 'read');
  const navigate = useNavigate();
  const params = useParams();
  const monitorSlotId = decodeSlotParam(params['*']);

  const [detail, setDetail] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsOffset, setRunsOffset] = useState(0);
  const [runsLoading, setRunsLoading] = useState(false);
  const [monitoring, setMonitoring] = useState(null);
  const [timingProfile, setTimingProfile] = useState(null);
  const [timingMissing, setTimingMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const loadCore = useCallback(async ({ silent = false } = {}) => {
    if (!canView || !monitorSlotId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const job = await fetchSapJobDetail(monitorSlotId);
      setDetail(job);
      setMonitoring(job.monitoring || null);
      if (job.timing_profile) {
        setTimingProfile(job.timing_profile);
        setTimingMissing(false);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load job');
      if (!silent) setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [canView, monitorSlotId]);

  const loadRuns = useCallback(async (offset = 0) => {
    if (!canView || !monitorSlotId) return;
    setRunsLoading(true);
    try {
      const data = await fetchSapJobRuns(monitorSlotId, { limit: 50, offset });
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
      setRunsOffset(offset);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [canView, monitorSlotId]);

  const loadMonitoring = useCallback(async () => {
    if (!canView || !monitorSlotId) return;
    try {
      const data = await fetchSapJobMonitoring(monitorSlotId);
      setMonitoring(data);
    } catch {
      setMonitoring({ available: false, reason: 'Monitoring data unavailable' });
    }
  }, [canView, monitorSlotId]);

  const loadTiming = useCallback(async () => {
    if (!canView || !monitorSlotId) return;
    setTimingMissing(false);
    try {
      const data = await fetchSapJobTimingProfile(monitorSlotId);
      setTimingProfile(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setTimingProfile(null);
        setTimingMissing(true);
      }
    }
  }, [canView, monitorSlotId]);

  useEffect(() => {
    loadCore();
    loadRuns(0);
    loadTiming();
  }, [loadCore, loadRuns, loadTiming]);

  useEffect(() => {
    if (!canView || !monitorSlotId) return undefined;
    const timer = setInterval(() => {
      loadCore({ silent: true });
      loadMonitoring();
    }, SAP_JOB_DETAIL_POLL_MS);
    return () => clearInterval(timer);
  }, [canView, monitorSlotId, loadCore, loadMonitoring]);

  useEffect(() => {
    if (activeTab === 'monitoring') loadMonitoring();
  }, [activeTab, loadMonitoring]);

  if (!canView) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You do not have permission to view this job.
        </CardContent>
      </Card>
    );
  }

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading job…
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="space-y-4" data-testid="sap-job-detail-error">
        <Button variant="ghost" onClick={() => navigate('/sap-job-monitoring')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to list
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const mon = monitoring || detail?.monitoring;
  const stage = mon?.available ? mon.stage : null;

  return (
    <div className="space-y-6" data-testid="sap-job-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sap-job-monitoring')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          SAP Job Monitoring
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">{detail?.job_name}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {detail?.sap_system} · client {detail?.sap_client}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <SapJobStatusBadge sapStatus={detail?.snapshot_status} monitoringStage={stage} />
          {detail?.planned_start && (
            <span className="text-sm text-muted-foreground">
              Planned {formatIsoDateTime(detail.planned_start)}
            </span>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="runs">Runs / History</TabsTrigger>
          <TabsTrigger value="evidence">Failure Evidence</TabsTrigger>
          <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
          <TabsTrigger value="timing">Timing Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2 text-sm">
              <div><span className="text-muted-foreground">Program</span><div className="font-mono">{detail?.program || '—'}</div></div>
              <div><span className="text-muted-foreground">Variant</span><div className="font-mono">{detail?.variant || '—'}</div></div>
              <div><span className="text-muted-foreground">Job count</span><div>{detail?.job_count || '—'}</div></div>
              <div><span className="text-muted-foreground">Monitor slot</span><div className="font-mono text-xs break-all">{detail?.monitor_slot_id}</div></div>
              <div><span className="text-muted-foreground">Latest run</span><div>{detail?.latest_run_id ? (
                <Link className="text-cp-accent hover:underline" to={runDetailPath(detail.latest_run_id)}>
                  Open run
                </Link>
              ) : '—'}</div></div>
              <div><span className="text-muted-foreground">Timing profile</span><div>{detail?.timing_profile?.valid ? 'Available' : 'Not enough data yet'}</div></div>
              <div className="sm:col-span-2"><span className="text-muted-foreground">Monitoring</span><div>{mon?.available ? (MONITORING_STAGE_LABELS[mon.stage] || mon.stage) : (mon?.reason || 'No active monitoring checkpoint')}</div></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Run history</CardTitle>
                <CardDescription>Recent executions for this job name.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => loadRuns(runsOffset)} disabled={runsLoading}>
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <div className="py-8 text-center text-muted-foreground">Loading runs…</div>
              ) : runs.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground" data-testid="sap-job-runs-empty">
                  No runs recorded yet.
                </div>
              ) : (
                <Table data-testid="sap-job-runs-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job count</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Planned</TableHead>
                      <TableHead>Actual start</TableHead>
                      <TableHead>Actual end</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Evidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.run_id}>
                        <TableCell>{run.job_count}</TableCell>
                        <TableCell><SapJobStatusBadge sapStatus={run.status} /></TableCell>
                        <TableCell>{formatIsoDateTime(run.planned_start)}</TableCell>
                        <TableCell>{formatIsoDateTime(run.actual_start)}</TableCell>
                        <TableCell>{formatIsoDateTime(run.actual_end)}</TableCell>
                        <TableCell>{formatDurationSeconds(run.duration_seconds)}</TableCell>
                        <TableCell className="font-mono text-xs">{run.program || '—'}</TableCell>
                        <TableCell>
                          {run.evidence_status || run.failure_message_summary ? (
                            <span className="text-amber-700 dark:text-amber-300 text-xs">Has evidence</span>
                          ) : '—'}
                          {' · '}
                          <Link to={runDetailPath(run.run_id)} className="text-cp-accent hover:underline text-xs">
                            View
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidence" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Failure evidence</CardTitle>
              <CardDescription>
                Open a canceled run from Run history for log lines and failure messages.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {detail?.latest_run_id ? (
                <Button asChild variant="outline">
                  <Link to={runDetailPath(detail.latest_run_id)}>View latest run detail</Link>
                </Button>
              ) : (
                <p className="text-muted-foreground text-sm">No runs available for evidence.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monitoring" className="mt-4">
          <SapJobMonitoringPanel monitoring={mon} snapshotStatus={detail?.snapshot_status} />
        </TabsContent>

        <TabsContent value="timing" className="mt-4">
          <SapJobTimingPanel timingProfile={timingProfile} timingMissing={timingMissing} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
